import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Save, User, Ghost, Eye, Compass, Award, Lightbulb, X, Zap, Brain, Pencil, Trash2, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { AutoExpandTextarea } from '../components/AutoExpandTextarea';
import StoryAuditor from '../components/StoryAuditor';
import AIMentor from '../components/AIMentor';
import UpgradeModal from '../components/UpgradeModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import { toast } from 'sonner';
import { authFetch } from '../lib/authFetch';
import { workspaceRoute } from '../lib/workspaceRoute';
import { useTranslation } from 'react-i18next';
import { createFormSave } from '../lib/formAutosave';
import { useFormAutosave } from '../hooks/useFormAutosave';
import SaveStatusLabel from '../components/SaveStatusLabel';
import {
  LEVEL_CONFIG, ROLE_LEVEL_TITLES, ANTAGONIST_TYPES, SERIES_LEVEL_META,
  getQuestions, ENNEAGRAM_PROFILE, ENNEAGRAM_WINGS, ENNEAGRAM_VARIANTS,
  ARC_HELPER_KEYS,
} from '../lib/characterQuestions';
import { SCROLL_CANVAS } from '../lib/featureFlags';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Route collapse (SCROLL_CANVAS): the 5-level wizard becomes a redirect shim
// into the character canvas, scrolled to the requested level. Cold bookmark
// loads of /character/:id/level/N land correctly. The wizard lives in its
// own component so the shim's early return never sits above its hooks.
export default function CharacterLevel() {
  const { projectId, levelNum } = useParams();
  if (SCROLL_CANVAS) {
    return (
      <Navigate
        to={`/character/${projectId}`}
        replace
        state={{ scrollToLevel: parseInt(levelNum, 10) || 0 }}
      />
    );
  }
  return <CharacterLevelWizard />;
}

function CharacterLevelWizard() {
  const { projectId, levelNum } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [project, setProject] = useState(null);
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false); // completion in flight
  const [showArcHelper, setShowArcHelper] = useState(false);
  const saveClientRef = useRef(null);
  const [showAuditor, setShowAuditor] = useState(false);
  const [showMentor, setShowMentor] = useState(false);
  const [characterProjectCount, setCharacterProjectCount] = useState(0);
  // Format of the workspace this character belongs to (tv_series → /series).
  const [workspaceFormat, setWorkspaceFormat] = useState(null);
  const [showVoicesModal, setShowVoicesModal] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [showDelete, setShowDelete] = useState(false);

  const currentLevel = parseInt(levelNum);
  const levelConfig = LEVEL_CONFIG[currentLevel];
  const LevelIcon = levelConfig?.icon;

  useEffect(() => {
    fetchData();
  }, [projectId, levelNum]);

  const fetchData = async () => {
    try {
      const [userRes, projectRes, projectsRes] = await Promise.all([
        authFetch(`${API}/auth/me`, { credentials: 'include' }),
        authFetch(`${API}/projects/${projectId}`, { credentials: 'include' }),
        authFetch(`${API}/projects`, { credentials: 'include' }),
      ]);
      if (!projectRes.ok) throw new Error(`load failed (${projectRes.status})`);

      const userData = await userRes.json();
      const projectData = await projectRes.json();
      const projectsData = await projectsRes.json();

      setUser(userData);
      setProject(projectData);
      const charCount = Array.isArray(projectsData)
        ? projectsData.filter(p => p.project_type === 'character').length
        : 0;
      setCharacterProjectCount(charCount);

      // Find the owning workspace project so "Back to Workspace" returns to
      // the right builder (Series Builder for tv_series, else the workspace).
      if (projectData?.workspace_id && Array.isArray(projectsData)) {
        const ws = projectsData.find(p => p.project_id === projectData.workspace_id);
        setWorkspaceFormat(ws?.format || null);
      }
      
      const levelKey = `level_${currentLevel}`;
      setFormData(projectData[levelKey] || {});
      // Re-key the save client when the project changes (param-only route
      // changes don't remount) — a stale client would PUT this project's
      // data at the OLD project's URL. Never regress a live client's rev
      // from a fetch: a GET served before our own in-flight PUT landed
      // carries the pre-rotation rev and adopting it 409-deadlocks saves.
      if (saveClientRef.current?.projectId !== projectId) {
        saveClientRef.current = {
          projectId,
          client: createFormSave({
            url: `${API}/projects/${projectId}`,
            initialRev: projectData.content_rev ?? null,
          }),
        };
      } else if (saveClientRef.current.client.getRev() == null) {
        saveClientRef.current.client.setRev(projectData.content_rev ?? null);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      // Disable autosave against a page we failed to load — otherwise the
      // previous level's formData could be saved under this level's key.
      setProject(null);
      setFormData({});
      toast.error('Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  // The autosaved payload pins the level it belongs to: the saver is
  // recreated when currentLevel changes (via the useCallback dep), and the
  // outgoing saver's final flush still holds the OLD level + OLD form data.
  const persistLevel = useCallback(
    (data, meta) =>
      saveClientRef.current.client.save({ level: currentLevel, data, completed: false }, meta),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectId, currentLevel],
  );
  const { status: saveStatus, markDirty, flush, isDirty } = useFormAutosave({
    data: formData,
    save: persistLevel,
    enabled: !loading && !!project,
  });

  // Level tabs leave this level's formData behind — make sure it landed
  // (or keep the writer here with their text) before navigating.
  const goToLevel = async (level) => {
    if (level === currentLevel) return;
    await flush();
    if (isDirty()) {
      toast.error(t('autosave_navBlocked'));
      return;
    }
    navigate(`/character/${projectId}/level/${level}`);
  };

  const handleInputChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    markDirty();
  };

  const startRename = () => { setNameInput(project?.title || ''); setEditingName(true); };
  const cancelRename = () => { setEditingName(false); setNameInput(''); };

  const saveRename = async () => {
    const title = nameInput.trim();
    if (!title || title === project?.title) { cancelRename(); return; }
    try {
      const res = await authFetch(`${API}/projects/${projectId}/rename`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ title }),
      });
      if (!res.ok) throw new Error();
      setProject((prev) => (prev ? { ...prev, title } : prev));
      toast.success('Character renamed');
      cancelRename();
    } catch {
      toast.error('Failed to rename character');
    }
  };

  const confirmDelete = async () => {
    try {
      const res = await authFetch(`${API}/projects/${projectId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error();
      toast.success('Character deleted');
      setShowDelete(false);
      navigate(
        project?.workspace_id
          ? workspaceRoute(project.workspace_id, workspaceFormat, 'characters')
          : '/dashboard?type=character'
      );
    } catch {
      toast.error('Failed to delete character');
    }
  };

  const handleRoleChange = async (role) => {
    try {
      const response = await authFetch(`${API}/projects/${projectId}/role`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ character_role: role }),
      });

      if (response.status === 403) {
        setShowVoicesModal(true);
        return;
      }

      if (!response.ok) throw new Error('Failed to update role');

      const updated = await response.json();
      setProject(updated);
      toast.success(`Role updated to ${role}`);
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error('Failed to update role');
    }
  };

  const handleArcTypeChange = async (arcType) => {
    try {
      const response = await authFetch(`${API}/projects/${projectId}/arc-type`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ arc_type: arcType }),
      });

      if (!response.ok) throw new Error('Failed to update arc type');

      const updated = await response.json();
      setProject(updated);
      toast.success(`Arc type updated to ${arcType}`);
    } catch (error) {
      console.error('Error updating arc type:', error);
      toast.error('Failed to update arc type');
    }
  };

  const handleAntagonistTypeChange = async (antagonistType) => {
    try {
      const response = await authFetch(`${API}/projects/${projectId}/antagonist-type`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ antagonist_type: antagonistType }),
      });

      if (!response.ok) throw new Error('Failed to update antagonist type');

      const updated = await response.json();
      setProject(updated);
      toast.success(`Antagonist type updated`);
    } catch (error) {
      console.error('Error updating antagonist type:', error);
      toast.error('Failed to update antagonist type');
    }
  };

  // Persist core type + wing + variant together. `patch` overrides; the rest
  // falls back to whatever is already on the project.
  const saveEnneagram = async (patch) => {
    const payload = {
      enneagram_type: patch.enneagram_type ?? (project?.enneagram_type || ''),
      enneagram_wing: patch.enneagram_wing ?? (project?.enneagram_wing || ''),
      enneagram_variant: patch.enneagram_variant ?? (project?.enneagram_variant || ''),
    };
    if (!payload.enneagram_type) return; // a core type is required first
    try {
      const response = await authFetch(`${API}/projects/${projectId}/enneagram`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (response.status === 403) {
        toast.error('Upgrade to Pro to use the Enneagram');
        return;
      }
      if (!response.ok) throw new Error('Failed to update');
      const updated = await response.json();
      setProject(updated);
      toast.success('Enneagram updated — syncs to Plot Forge');
    } catch (error) {
      console.error('Error updating enneagram:', error);
      toast.error('Failed to update enneagram');
    }
  };

  const handleEnneagramChange = (e) => {
    const type = e.target.value;
    if (!type) return;
    // A wing must be adjacent to the new core type — drop it if it no longer fits.
    const keepWing = (ENNEAGRAM_WINGS[type] || []).includes(project?.enneagram_wing)
      ? project.enneagram_wing : '';
    saveEnneagram({ enneagram_type: type, enneagram_wing: keepWing });
  };
  const handleEnneagramWing = (e) => saveEnneagram({ enneagram_wing: e.target.value });
  const handleEnneagramVariant = (e) => saveEnneagram({ enneagram_variant: e.target.value });

  // Explicit flush — autosave already persists on a debounce; this is the
  // "save right now" button, not the only path to persistence.
  const handleSave = () => flush();

  const handleComplete = async () => {
    const questions = getQuestions(levelConfig, project?.character_role || 'protagonist', project?.arc_type, project?.antagonist_type, currentLevel, workspaceFormat === 'tv_series');
    const requiredQuestions = questions.filter(q => !q.optional);
    const allFilled = requiredQuestions.every((q) => formData[q.key]?.trim());

    if (!allFilled) {
      toast.error('Please fill in all required fields before completing this level');
      return;
    }

    setSaving(true);
    try {
      await flush(); // latest edits land before the completion write
      const client = saveClientRef.current?.client;
      const response = await authFetch(`${API}/projects/${projectId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          level: currentLevel,
          data: formData,
          completed: true,
          base_rev: client?.getRev() ?? null,
        }),
      });

      if (response.status === 403) {
        navigate('/upgrade');
        return;
      }

      if (response.status === 409) {
        toast.error(t('saveStatus_conflict'));
        return;
      }

      if (!response.ok) throw new Error('Failed to complete level');

      const updatedProject = await response.json();
      client?.setRev(updatedProject.content_rev ?? null);
      setProject(updatedProject);
      toast.success(`Level ${currentLevel} completed! Badge earned: ${levelConfig.badge}`);

      if (currentLevel < 4) {
        setTimeout(() => {
          navigate(`/character/${projectId}/level/${currentLevel + 1}`);
        }, 1500);
      } else {
        setTimeout(() => {
          navigate(
            project?.workspace_id
              ? workspaceRoute(project.workspace_id, workspaceFormat, 'characters')
              : '/dashboard?type=character'
          );
        }, 1500);
      }
    } catch (error) {
      console.error('Error completing level:', error);
      toast.error('Failed to complete level');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !levelConfig) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-foreground font-mono">Loading...</div>
      </div>
    );
  }

  const role = project?.character_role || 'protagonist';
  const roleOverride = ROLE_LEVEL_TITLES[role]?.[currentLevel];
  const isSeries = workspaceFormat === 'tv_series';
  const seriesMeta = isSeries ? SERIES_LEVEL_META[currentLevel] : null;
  const displayTitle = seriesMeta?.title || t(roleOverride?.titleKey || levelConfig.titleKey);
  const displaySubtitle = seriesMeta?.subtitle || t(roleOverride?.subtitleKey || levelConfig.subtitleKey);
  const questions = getQuestions(levelConfig, role, project?.arc_type, project?.antagonist_type, currentLevel, isSeries);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="noise-overlay" />

      <header className="glassmorphism sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate(
              project?.workspace_id
                ? workspaceRoute(project.workspace_id, workspaceFormat, 'characters')
                : '/dashboard?type=character'
            )}
            data-testid="back-to-workspace-btn"
            className="rounded-none font-mono uppercase tracking-widest text-xs"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {project?.workspace_id ? 'Back to Workspace' : t('nav_backToDashboard')}
          </Button>
          <div className="flex items-center gap-2">
            {editingName ? (
              <div className="flex items-center gap-1">
                <Input
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter') saveRename(); if (e.key === 'Escape') cancelRename(); }}
                  className="h-8 w-48 bg-background border-input rounded-none font-mono text-sm"
                  data-testid="rename-char-input"
                />
                <Button size="sm" onClick={saveRename} className="h-8 w-8 p-0 bg-primary text-primary-foreground rounded-none" aria-label="Save name" data-testid="save-char-name-btn">
                  <Check className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={cancelRename} className="h-8 w-8 p-0 rounded-none" aria-label="Cancel rename">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <>
                <div className="text-sm font-mono" data-testid="project-title">
                  {project?.title}
                </div>
                <Button variant="ghost" size="sm" onClick={startRename} className="h-7 w-7 p-0" aria-label="Rename character" data-testid="rename-char-btn">
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setShowDelete(true)} className="h-7 w-7 p-0" aria-label="Delete character" data-testid="delete-char-btn">
                  <Trash2 className="w-3.5 h-3.5 text-destructive" />
                </Button>
              </>
            )}
          </div>
        </div>
        
        {/* Level Tab Navigation — visible to all users */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
          <div className="flex flex-wrap items-center justify-between gap-y-2 border-t border-border/20 pt-4">
            <div className="flex items-center gap-1" data-testid="pro-tab-navigation">
              {[0, 1, 2, 3, 4].map((level) => {
                const config = LEVEL_CONFIG[level];
                const Icon = config.icon;
                const isActive = currentLevel === level;
                const isCompleted = project?.badges?.includes(config.badge);

                return (
                  <button
                    key={level}
                    onClick={() => goToLevel(level)}
                    data-testid={`tab-level-${level}`}
                    className={`flex items-center gap-2 px-2.5 sm:px-4 py-2 font-mono text-xs uppercase tracking-wider transition-all cursor-pointer
                      ${isActive
                        ? 'bg-primary text-primary-foreground'
                        : isCompleted
                          ? 'bg-primary/20 text-primary hover:bg-primary/30'
                          : 'bg-background/50 text-muted-foreground hover:bg-background/80 hover:text-foreground'}
                    `}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="hidden sm:inline">{level}</span>
                    {isCompleted && <span className="text-[10px]">✓</span>}
                  </button>
                );
              })}
            </div>

            {/* AI Mentor + Stress Test: free for first character, Indie+ for additional */}
            {(characterProjectCount <= 1 || ['indie', 'studio'].includes(user?.subscription_tier)) && (
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => setShowMentor(true)}
                  data-testid="ai-mentor-btn"
                  variant="outline"
                  className="rounded-none font-mono uppercase tracking-widest text-xs px-4 py-2 border-primary/50 text-primary hover:bg-primary/10"
                >
                  <Brain className="w-4 h-4 mr-2" />
                  {t('aiMentor')}
                </Button>
                <Button
                  onClick={() => setShowAuditor(true)}
                  data-testid="stress-test-btn"
                  variant="outline"
                  className="rounded-none font-mono uppercase tracking-widest text-xs px-4 py-2 border-primary/50 text-primary hover:bg-primary/10"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  {t('stressTest')}
                </Button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-12 text-center">
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="relative">
              <div className="w-20 h-20 border-4 border-primary/30 rounded-full flex items-center justify-center">
                <LevelIcon className="w-10 h-10 text-primary" />
              </div>
              <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-primary rounded-full flex items-center justify-center font-mono text-sm font-bold">
                {currentLevel}
              </div>
            </div>
          </div>
          <h1 className="font-mono text-3xl md:text-4xl font-semibold tracking-tight mb-2" data-testid="level-title">
            {displayTitle}
          </h1>
          <p className="text-primary text-lg font-mono">{displaySubtitle}</p>
        </div>

        <div className="mb-12">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-mono">{t('plot_overallProgress')}</span>
            <span className="text-sm font-mono">{project?.progress || 0}%</span>
          </div>
          <div className="relative h-2 bg-secondary rounded-none overflow-hidden">
            <div className="absolute inset-y-0 left-0 bg-primary" style={{ width: `${project?.progress || 0}%` }} />
          </div>
        </div>

        {/* Arc Helper Button - Level 1 Protagonist Only */}
        {currentLevel === 1 && role === 'protagonist' && (
          <div className="mb-8 flex justify-end">
            <Button
              onClick={() => setShowArcHelper(!showArcHelper)}
              data-testid="arc-helper-btn"
              variant="outline"
              className={`rounded-none font-mono uppercase tracking-widest text-xs px-4 py-2 ${showArcHelper ? 'bg-primary/20 border-primary' : ''}`}
            >
              <Lightbulb className="w-4 h-4 mr-2" />
              {t('arcHelper')}
            </Button>
          </div>
        )}

        <div className="flex flex-col md:flex-row gap-8">
          {/* Arc Helper Sidebar - Level 1 Protagonist Only */}
          {currentLevel === 1 && role === 'protagonist' && showArcHelper && (
            <div className="w-full md:w-80 flex-shrink-0 bg-card border border-border/40 p-6 h-fit md:sticky top-32" data-testid="arc-helper-sidebar">
              {(() => {
                const arcKeys = ARC_HELPER_KEYS[project?.arc_type || 'positive'];
                return (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-mono text-lg font-semibold text-primary">
                        {t(arcKeys.titleKey)}
                      </h3>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowArcHelper(false)}
                        className="h-6 w-6 p-0"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>

                    <p className="text-sm text-muted-foreground mb-6">
                      {t(arcKeys.descKey)}
                    </p>

                    <div className="space-y-4">
                      <div>
                        <h4 className="font-mono text-xs uppercase text-primary mb-2">{t('keyElements')}</h4>
                        <ul className="space-y-2">
                          {arcKeys.guidanceKeys.map((k, i) => (
                            <li key={i} className="text-xs text-foreground flex items-start gap-2">
                              <span className="text-primary mt-0.5">•</span>
                              {t(k)}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="pt-4 border-t border-border/40">
                        <h4 className="font-mono text-xs uppercase text-primary mb-2">{t('proTips')}</h4>
                        <ul className="space-y-2">
                          {arcKeys.tipsKeys.map((k, i) => (
                            <li key={i} className="text-xs text-muted-foreground italic flex items-start gap-2">
                              <span className="text-primary/60 mt-0.5">→</span>
                              {t(k)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          <div className={`flex-1 space-y-8 mb-12 ${currentLevel === 1 && role === 'protagonist' && showArcHelper ? '' : 'max-w-4xl mx-auto'}`}>
          {/* Level 0: Role & Arc Type Dropdowns */}
          {currentLevel === 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-b border-border/40 pb-8">
              <div className="space-y-3">
                <label htmlFor="character_role" className="block font-mono text-sm uppercase tracking-wider text-primary">
                  {t('role')}
                </label>
                <select
                  id="character_role"
                  value={project?.character_role || 'protagonist'}
                  onChange={(e) => handleRoleChange(e.target.value)}
                  data-testid="role-select"
                  className="w-full bg-background border border-input rounded-none font-mono focus:ring-2 focus:ring-primary focus:border-transparent px-4 py-3 text-foreground"
                >
                  <option value="protagonist">{t('protagonist')}</option>
                  <option value="antagonist">{t('antagonist')}</option>
                  <option value="supporting">{t('supporting')}</option>
                </select>
                <p className="text-xs text-muted-foreground">
                  {t('char_roleDesc')}
                </p>
              </div>
              
              <div className="space-y-3">
                <label htmlFor="arc_type" className="block font-mono text-sm uppercase tracking-wider text-primary">
                  {t('arcType')}
                </label>
                <select
                  id="arc_type"
                  value={project?.arc_type || 'positive'}
                  onChange={(e) => handleArcTypeChange(e.target.value)}
                  data-testid="arc-type-select"
                  className="w-full bg-background border border-input rounded-none font-mono focus:ring-2 focus:ring-primary focus:border-transparent px-4 py-3 text-foreground"
                >
                  <option value="positive">{t('changePositive')}</option>
                  <option value="negative">{t('fallNegative')}</option>
                  <option value="flat">{t('flat')}</option>
                </select>
                <p className="text-xs text-muted-foreground">
                  {t('char_arcTypeDesc')}
                </p>
              </div>

              {/* Antagonist Type Selector — only when role is antagonist */}
              {role === 'antagonist' && (
                <div className="space-y-3 md:col-span-2">
                  <label className="block font-mono text-sm uppercase tracking-wider text-primary">
                    Opposition Type
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {ANTAGONIST_TYPES.map((type) => {
                      const isSelected = (project?.antagonist_type || 'person') === type.value;
                      return (
                        <button
                          key={type.value}
                          type="button"
                          onClick={() => handleAntagonistTypeChange(type.value)}
                          className={`text-left p-4 border font-mono transition-all ${
                            isSelected
                              ? 'border-primary bg-primary/10 text-foreground'
                              : 'border-border/40 bg-background/50 text-muted-foreground hover:border-primary/50 hover:text-foreground'
                          }`}
                        >
                          <div className="text-xs uppercase tracking-widest font-semibold mb-1">
                            {type.label}
                          </div>
                          <div className="text-xs leading-relaxed opacity-80">
                            {type.desc}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {questions.map((question) => (
            <div key={question.key} className="space-y-3">
              <label htmlFor={question.key} className="block font-mono text-sm uppercase tracking-wider text-foreground">
                {question.label || t(question.labelKey)}
              </label>
              {question.short ? (
                // Identity fields only (name, age, gender, ethnicity) — stay single-line.
                <Input
                  id={question.key}
                  type="text"
                  placeholder={question.placeholder || t(question.placeholderKey)}
                  value={formData[question.key] || ''}
                  onChange={(e) => handleInputChange(question.key, e.target.value)}
                  data-testid={`input-${question.key}`}
                  className="bg-background border-input rounded-none font-mono focus:ring-2 focus:ring-primary focus:border-transparent placeholder:text-muted-foreground/50"
                />
              ) : (
                // All story-content fields auto-expand. min-h is larger when the
                // question was originally tagged multiline (long-form prompts).
                <AutoExpandTextarea
                  id={question.key}
                  placeholder={question.placeholder || t(question.placeholderKey)}
                  value={formData[question.key] || ''}
                  onChange={(e) => handleInputChange(question.key, e.target.value)}
                  data-testid={`input-${question.key}`}
                  className={`bg-background border-input rounded-none font-mono focus:ring-2 focus:ring-primary focus:border-transparent placeholder:text-muted-foreground/50 ${
                    question.multiline ? 'min-h-[120px]' : 'min-h-[80px]'
                  }`}
                />
              )}
            </div>
          ))}
          
          {currentLevel === 1 && (
            <div className="space-y-3 border-t border-border/40 pt-8">
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="enneagram" className="block font-mono text-sm uppercase tracking-wider text-primary">
                  {t('char_enneagramLabel')} {!['indie', 'studio'].includes(user?.subscription_tier) && '(Indie+)'}
                </label>
                {!['indie', 'studio'].includes(user?.subscription_tier) && (
                  <Button
                    size="sm"
                    onClick={() => navigate('/upgrade')}
                    className="bg-primary/20 text-primary hover:bg-primary/30 rounded-none font-mono uppercase tracking-widest text-xs px-3 py-1"
                  >
                    {t('char_upgradeToIndie')}
                  </Button>
                )}
              </div>
              <select
                id="enneagram"
                value={project?.enneagram_type || ''}
                onChange={handleEnneagramChange}
                disabled={!['indie', 'studio'].includes(user?.subscription_tier)}
                data-testid="enneagram-select"
                className={`w-full bg-background border border-input rounded-none font-mono focus:ring-2 focus:ring-primary focus:border-transparent px-4 py-3 text-foreground ${!['indie', 'studio'].includes(user?.subscription_tier) ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <option value="">{t('char_enneagramPh')}</option>
                <option value="1">{t('enn_type1')}</option>
                <option value="2">{t('enn_type2')}</option>
                <option value="3">{t('enn_type3')}</option>
                <option value="4">{t('enn_type4')}</option>
                <option value="5">{t('enn_type5')}</option>
                <option value="6">{t('enn_type6')}</option>
                <option value="7">{t('enn_type7')}</option>
                <option value="8">{t('enn_type8')}</option>
                <option value="9">{t('enn_type9')}</option>
              </select>

              {/* Depth: Wing + Instinctual Variant */}
              {project?.enneagram_type && ['indie', 'studio'].includes(user?.subscription_tier) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                  <div>
                    <label htmlFor="enneagram-wing" className="block font-mono text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Wing</label>
                    <select
                      id="enneagram-wing"
                      value={project?.enneagram_wing || ''}
                      onChange={handleEnneagramWing}
                      data-testid="enneagram-wing-select"
                      className="w-full bg-background border border-input rounded-none font-mono text-sm px-3 py-2 text-foreground focus:ring-2 focus:ring-primary"
                    >
                      <option value="">No wing</option>
                      {(ENNEAGRAM_WINGS[project.enneagram_type] || []).map(w => (
                        <option key={w} value={w}>{project.enneagram_type}w{w} — {ENNEAGRAM_PROFILE[w]?.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="enneagram-variant" className="block font-mono text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Instinctual Variant</label>
                    <select
                      id="enneagram-variant"
                      value={project?.enneagram_variant || ''}
                      onChange={handleEnneagramVariant}
                      data-testid="enneagram-variant-select"
                      className="w-full bg-background border border-input rounded-none font-mono text-sm px-3 py-2 text-foreground focus:ring-2 focus:ring-primary"
                    >
                      <option value="">No subtype</option>
                      {ENNEAGRAM_VARIANTS.map(v => (
                        <option key={v.value} value={v.value}>{v.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Depth panel — desire / fear / growth / stress */}
              {project?.enneagram_type && ENNEAGRAM_PROFILE[project.enneagram_type] && ['indie', 'studio'].includes(user?.subscription_tier) && (
                <div className="mt-3 border border-primary/30 bg-primary/5 p-4 space-y-1.5" data-testid="enneagram-depth">
                  <p className="font-mono text-xs uppercase tracking-wider text-primary">
                    Type {project.enneagram_type}{project.enneagram_wing ? `w${project.enneagram_wing}` : ''} · {ENNEAGRAM_PROFILE[project.enneagram_type].name}
                    {project.enneagram_variant ? ` · ${ENNEAGRAM_VARIANTS.find(v => v.value === project.enneagram_variant)?.label}` : ''}
                  </p>
                  <p className="text-xs text-muted-foreground font-mono"><span className="text-foreground">Core desire:</span> {ENNEAGRAM_PROFILE[project.enneagram_type].desire}</p>
                  <p className="text-xs text-muted-foreground font-mono"><span className="text-foreground">Core fear:</span> {ENNEAGRAM_PROFILE[project.enneagram_type].fear}</p>
                  <p className="text-xs text-muted-foreground font-mono"><span className="text-green-400">In growth:</span> {ENNEAGRAM_PROFILE[project.enneagram_type].growth}</p>
                  <p className="text-xs text-muted-foreground font-mono"><span className="text-amber-500">Under stress:</span> {ENNEAGRAM_PROFILE[project.enneagram_type].stress}</p>
                  {project.enneagram_variant && (
                    <p className="text-xs text-muted-foreground font-mono"><span className="text-foreground">Subtype focus:</span> {ENNEAGRAM_VARIANTS.find(v => v.value === project.enneagram_variant)?.desc}</p>
                  )}
                </div>
              )}

              {project?.enneagram_type && ['indie', 'studio'].includes(user?.subscription_tier) && (
                <p className="text-xs text-muted-foreground mt-2">
                  {t('char_enneagramSyncNote')}
                </p>
              )}
              {!['indie', 'studio'].includes(user?.subscription_tier) && (
                <p className="text-xs text-muted-foreground italic">
                  {t('char_enneagramLockedNote')}
                </p>
              )}
            </div>
          )}
        </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="save-btn"
              variant="outline"
              className="rounded-none font-mono uppercase tracking-widest text-xs px-6 py-3"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? t('saving') : t('saveProgress')}
            </Button>
            <SaveStatusLabel status={saveStatus} />
          </div>
          <Button
            onClick={handleComplete}
            disabled={saving}
            data-testid="complete-level-btn"
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-mono uppercase tracking-widest text-xs px-6 py-3 shadow-glow transition-all hover:shadow-glow-strong"
          >
            {currentLevel < 4 ? (
              <>
                {t('char_completeContinue')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            ) : (
              t('char_completeCharacter')
            )}
          </Button>
        </div>
      </main>
      
      {/* Story Auditor Modal */}
      {showAuditor && (
        <StoryAuditor
          projectId={projectId}
          projectType="character"
          isPro={characterProjectCount <= 1 || ['indie', 'studio'].includes(user?.subscription_tier)}
          onClose={() => setShowAuditor(false)}
        />
      )}

      {/* AI Mentor Sidebar */}
      <AIMentor
        isOpen={showMentor}
        onClose={() => setShowMentor(false)}
        projectId={projectId}
        currentLevel={currentLevel}
        isPro={characterProjectCount <= 1 || ['indie', 'studio'].includes(user?.subscription_tier)}
      />

      {showVoicesModal && (
        <UpgradeModal
          title="Your story needs a full cast."
          description="You've built your Protagonist and Antagonist. Upgrade to add supporting characters, love interests, and more."
          benefits={[
            'Unlimited character projects',
            'Supporting characters & ensemble cast',
            'Full Character Web',
            'Cross-character personality syncing',
          ]}
          ctaText="Unlock Indie"
          onClose={() => setShowVoicesModal(false)}
        />
      )}

      <DeleteConfirmModal
        isOpen={showDelete}
        onClose={() => setShowDelete(false)}
        onConfirm={confirmDelete}
        projectName={project?.title || ''}
      />
    </div>
  );
}
